import montserratItalic from "@/assets/fonts/montserrat/Montserrat-Italic.ttf?url";
import montserratMedium from "@/assets/fonts/montserrat/Montserrat-Medium.ttf?url";
import montserratRegular from "@/assets/fonts/montserrat/Montserrat-Regular.ttf?url";
import montserratSemiBold from "@/assets/fonts/montserrat/Montserrat-SemiBold.ttf?url";
import {
    Document,
    Font,
    Image,
    Page,
    Path,
    StyleSheet,
    Svg,
    Text,
    View,
} from "@react-pdf/renderer";
import { formatDate, formatDateDuration } from "@sr-partner-portal/common";
import type { FC, JSX } from "react";
import type { ResumeContent } from "../context";
import type { JobExperience, LanguageSkill, Training } from "../types";
import { languageSkillLevelMap } from "../resume-step-2/constants";

// Register font
Font.register({
    family: "Montserrat",
    fonts: [
        { src: montserratRegular },
        {
            src: montserratMedium,
            fontWeight: "medium",
        },
        {
            src: montserratSemiBold,
            fontWeight: "semibold",
        },
        {
            src: montserratItalic,
            fontStyle: "italic",
        },
    ],
});

const styles = StyleSheet.create({
    page: {
        flexDirection: "column",
        backgroundColor: "#ffffff",
        gap: 24,
        paddingVertical: 24,
        paddingHorizontal: 24,
        fontFamily: "Montserrat",
    },
    pageCount: {
        position: "absolute",
        bottom: 18,
        right: 18,
        fontSize: 8,
        color: "#727272",
        textAlign: "right",
        fontStyle: "italic",
    },
    section: {
        gap: 10,
    },
    title: {
        fontSize: 13,
        textTransform: "uppercase",
        fontWeight: "semibold",
        marginBottom: 15,
        borderBottomWidth: 2,
        borderBottomColor: "#D9D9D9",
        paddingBottom: 5,
        width: "100%",
    },
    text: {
        fontSize: 11,
        color: "#333333",
        lineHeight: 1.25,
    },
    // Start: info header
    header: {
        top: -24,
        left: -24,
        width: 595.28, // A4 width
        flexDirection: "row",
    },
    headline: {
        fontSize: 17,
        marginTop: 18,
        fontWeight: "semibold",
        textTransform: "uppercase",
        lineHeight: 1,
    },
    name: {
        fontSize: 24,
        marginBottom: 24,
        fontWeight: "semibold",
        textTransform: "uppercase",
        lineHeight: 1,
    },
    contactInfo: {
        display: "flex",
        flexDirection: "column",
        gap: 5,
    },
    infoContainer: {
        flex: 6,
        paddingTop: 24,
        paddingHorizontal: 24,
        position: "relative",
        gap: 5,
    },
    infoRow: {
        display: "flex",
        flexDirection: "row",
        gap: 20,
    },
    infoTitleCol: {
        flexDirection: "row",
        gap: 5,
        width: 100,
        flexShrink: 0,
        alignItems: "center",
    },
    infoCol: {
        flex: 1,
    },
    imageContainer: {
        flex: 5,
        position: "relative",
        alignItems: "flex-end",
        zIndex: -1,
    },
    branding: {
        width: "100%",
        height: 150,
        position: "absolute",
    },
    image: {
        width: 135,
        height: 135,
        marginTop: 42,
        marginRight: 48,
    },
    // End: info header
    experienceContainer: {
        flexDirection: "column",
        gap: 10,
    },
    experienceHeader: {
        flexDirection: "row",
        gap: 10,
    },
    dateCol: {
        width: 200,
        flexShrink: 0,
    },
    date: {
        fontSize: 11,
        lineHeight: 1.25,
        fontWeight: "semibold",
        color: "#333333",
    },
    companyCol: {
        flex: 1,
        gap: 3,
    },
    companyName: {
        fontSize: 11,
        lineHeight: 1.25,
        fontWeight: "semibold",
        color: "#333333",
    },
    position: {
        fontSize: 9,
        lineHeight: 1.25,
        color: "#333333",
    },
    description: {
        marginTop: 3,
        flexDirection: "column",
        gap: 5,
    },
    descriptionRow: {
        flexDirection: "row",
        gap: 5,
    },
    descriptionText: {
        color: "#333333",
        lineHeight: 1.25,
        fontSize: 9,
    },
    skillList: {
        flexDirection: "row",
        gap: 10,
    },
    skillListTitleCol: {
        width: 200,
        flexShrink: 0,
    },
    skillListTitleText: {
        fontSize: 11,
        fontWeight: "semibold",
    },
    skillListListCol: {
        flex: 1,
        gap: 3,
    },
    skillListWrapper: {
        gap: 15,
    },
    list: {
        flexDirection: "column",
        gap: 5,
    },
    listItem: {
        flexDirection: "row",
        gap: 5,
    },
    listItemText: {
        color: "#333333",
        lineHeight: 1.25,
        fontSize: 11,
    },
    listItemBulletPoint: {
        color: "#333333",
        lineHeight: 1.25,
        fontSize: 11,
        fontWeight: "semibold",
    },
    languageList: {
        flexDirection: "row",
        gap: 10,
    },
    languageListListCol: {
        flexDirection: "column",
        flex: 1,
        gap: 15,
    },
    languageItem: {
        flexDirection: "column",
        gap: 5,
    },
    languageTitleRow: {
        flexDirection: "row",
        justifyContent: "space-between",
    },
    languageTitle: {
        fontSize: 11,
        fontWeight: "semibold",
        color: "#333333",
    },
    languageLevelTitle: {
        fontSize: 11,
        color: "#333333",
    },
    languageLevelTileList: {
        flexDirection: "row",
        gap: 5,
    },
    languageLevelTile: {
        height: 8,
        flex: 1,
        backgroundColor: "#A3A3A3",
        borderRadius: 2,
    },
});

Font.registerHyphenationCallback((word) => [word]);

const Experience: FC<{ experience: Training | JobExperience }> = ({
    experience,
}) => (
    <View style={styles.experienceHeader} wrap={false}>
        <View style={styles.dateCol}>
            <Text style={styles.date}>
                {`${experience.isCurrent ? "Seit " : ""}${formatDate(experience.startDate, "MM/yyyy")}${experience.isCurrent ? "" : ` - ${formatDate(experience.endDate, "MM/yyyy")}`}`}
            </Text>
            {!experience.isCurrent && (
                <Text style={styles.descriptionText}>
                    {formatDateDuration(
                        experience.startDate,
                        experience.endDate,
                    )}
                </Text>
            )}
        </View>
        <View style={styles.companyCol}>
            <Text style={styles.companyName}>{experience.company}</Text>
            <Text style={styles.position}>
                {"position" in experience
                    ? experience.position
                    : experience.title}
            </Text>

            {experience.description && (
                <View style={styles.description}>
                    {experience.description
                        .split("• ")
                        .slice(1)
                        .map((line) => (
                            <View key={line} style={styles.descriptionRow}>
                                <Text style={styles.descriptionText}>•</Text>
                                <Text
                                    style={{
                                        ...styles.descriptionText,
                                        flex: 1,
                                    }}
                                >
                                    {line}
                                </Text>
                            </View>
                        ))}
                </View>
            )}
        </View>
    </View>
);

const SkillList: FC<{ values: string[]; title: string }> = ({
    values,
    title,
}) => (
    <View style={styles.skillList}>
        <View style={styles.skillListTitleCol}>
            <Text style={styles.skillListTitleText}>
                {title}
            </Text>
        </View>
        <View style={styles.skillListListCol}>
            {values.map((value) => (
                <View key={value} style={styles.listItem}>
                    <Text style={styles.listItemBulletPoint}>•</Text>
                    <Text
                        style={{
                            ...styles.listItemText,
                            flex: 1,
                        }}
                    >
                        {value}
                    </Text>
                </View>
            ))}
        </View>
    </View>
);

const LanguageLevel = ({
    level,
    targetLevel,
}: {
    level: number;
    targetLevel: number;
}) => (
    <View
        style={{
            ...styles.languageLevelTile,
            opacity: level >= targetLevel ? 1 : 0.3,
        }}
    />
)

const LanguageList: FC<{ languageSkills: LanguageSkill[]; title: string }> = ({
    languageSkills,
    title,
}) => (
    <View style={styles.languageList} wrap={false}>
        <View style={styles.skillListTitleCol}>
            <Text style={styles.skillListTitleText}>{title}</Text>
        </View>
        <View style={styles.languageListListCol}>
            {languageSkills.map((item) => (
                <View key={item.language} style={styles.languageItem}>
                    <View style={styles.languageTitleRow}>
                        <Text style={styles.languageTitle}>
                            {item.language}
                        </Text>
                        <Text style={styles.languageLevelTitle}>
                            {languageSkillLevelMap[item.level]}
                        </Text>
                    </View>
                    <View style={styles.languageLevelTileList}>
                        {[1, 2, 3, 4, 5].map((targetLevel) => (
                            <LanguageLevel
                                key={targetLevel}
                                level={item.level}
                                targetLevel={targetLevel}
                            />
                        ))}
                    </View>
                </View>
            ))}
        </View>
    </View>
);

const SonstigesSection: FC<{ step2: ResumeContent["step2"] }> = ({ step2 }) => {
    const groups = [
        step2.additionalSkills?.length > 0 && (
            <View key="additional" style={styles.list}>
                <SkillList values={step2.additionalSkills} title="Zusatzqualifikationen" />
            </View>
        ),
        step2.softwareSkills?.length > 0 && (
            <View key="software" style={styles.list}>
                <SkillList values={step2.softwareSkills} title="Softwarekenntnisse" />
            </View>
        ),
        step2.drivingLicenses?.length > 0 && (
            <View key="driving" style={styles.list}>
                <SkillList values={step2.drivingLicenses} title="Führerschein" />
            </View>
        ),
        step2.hobbies?.length > 0 && (
            <View key="hobbies" style={styles.list}>
                <SkillList values={step2.hobbies} title="Hobbies" />
            </View>
        ),
        step2.languages?.length > 0 && (
            <View key="languages" style={styles.list}>
                <LanguageList languageSkills={step2.languages} title="Sprachkenntnisse" />
            </View>
        ),
    ].filter(Boolean) as JSX.Element[];

    return (
        <View style={styles.section}>
            <View wrap={false}>
                <Text style={styles.title}>Sonstiges</Text>
                {groups[0]}
            </View>
            {groups.length > 1 && (
                <View style={styles.skillListWrapper}>
                    {groups.slice(1)}
                </View>
            )}
        </View>
    );
};

const ResumePDF = ({ resumeContent }: { resumeContent: ResumeContent }) => (
    <Document>
        <Page size="A4" style={styles.page}>
            <View style={styles.header}>
                <View style={styles.infoContainer}>
                    <Text style={styles.headline}>Lebenslauf</Text>
                    <Text style={styles.name}>{resumeContent.step1.name}</Text>
                    <View style={styles.contactInfo}>
                        {resumeContent.step1.birthDate && (
                            <View style={styles.infoRow}>
                                <View style={styles.infoTitleCol}>
                                    <Svg
                                        viewBox="0 0 24 24"
                                        width="14"
                                        height="14"
                                        stroke="#333333"
                                        strokeWidth={2}
                                    >
                                        <Path d="M4 5m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z" />
                                        <Path d="M16 3l0 4" />
                                        <Path d="M8 3l0 4" />
                                        <Path d="M4 11l16 0" />
                                        <Path d="M8 15h2v2h-2z" />
                                    </Svg>
                                    <Text style={styles.text}>
                                        Geburtsdatum
                                    </Text>
                                </View>
                                <View style={styles.infoCol}>
                                    <Text style={styles.text}>
                                        {formatDate(
                                            resumeContent.step1.birthDate,
                                            "dd.MM.yyyy",
                                        )}
                                    </Text>
                                </View>
                            </View>
                        )}
                        <View style={styles.infoRow}>
                            <View style={styles.infoTitleCol}>
                                <Svg
                                    viewBox="0 0 24 24"
                                    width="14"
                                    height="14"
                                    stroke="#333333"
                                    strokeWidth={2}
                                >
                                    <Path d="M9 11a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
                                    <Path d="M17.657 16.657l-4.243 4.243a2 2 0 0 1 -2.827 0l-4.244 -4.243a8 8 0 1 1 11.314 0z" />
                                </Svg>
                                <Text style={styles.text}>Adresse</Text>
                            </View>
                            <View style={styles.infoCol}>
                                <Text style={styles.text}>
                                    {resumeContent.step1.street}
                                </Text>
                                <Text style={styles.text}>
                                    {[
                                        resumeContent.step1.postalCode,
                                        resumeContent.step1.city,
                                    ]
                                        .filter(Boolean)
                                        .join(" ") || "–"}
                                </Text>
                            </View>
                        </View>
                        <View style={styles.infoRow}>
                            <View style={styles.infoTitleCol}>
                                <Svg
                                    viewBox="0 0 24 24"
                                    width="14"
                                    height="14"
                                    stroke="#333333"
                                    strokeWidth={2}
                                >
                                    <Path d="M5 4h4l2 5l-2.5 1.5a11 11 0 0 0 5 5l1.5 -2.5l5 2v4a2 2 0 0 1 -2 2a16 16 0 0 1 -15 -15a2 2 0 0 1 2 -2" />
                                </Svg>
                                <Text style={styles.text}>Telefon</Text>
                            </View>
                            <View style={styles.infoCol}>
                                <Text style={styles.text}>
                                    {resumeContent.step1.phone || "–"}
                                </Text>
                            </View>
                        </View>
                        <View style={styles.infoRow}>
                            <View style={styles.infoTitleCol}>
                                <Svg
                                    viewBox="0 0 24 24"
                                    width="14"
                                    height="14"
                                    stroke="#333333"
                                    strokeWidth={2}
                                >
                                    <Path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2v-10z" />
                                    <Path d="M3 7l9 6l9 -6" />
                                </Svg>
                                <Text style={styles.text}>E-Mail</Text>
                            </View>
                            <View style={styles.infoCol}>
                                <Text style={styles.text}>
                                    {resumeContent.step1.email || "–"}
                                </Text>
                            </View>
                        </View>
                        {resumeContent.step1.status && (
                            <View style={styles.infoRow}>
                                <View style={styles.infoTitleCol}>
                                    <Svg
                                        viewBox="0 0 24 24"
                                        width="14"
                                        height="14"
                                        stroke="#333333"
                                        strokeWidth={2}
                                    >
                                        <Path d="M12 5.5m-2.5 0a2.5 2.5 0 1 0 5 0a2.5 2.5 0 1 0 -5 0" />
                                        <Path d="M12 21.368l5.095 -5.096a3.088 3.088 0 1 0 -4.367 -4.367l-.728 .727l-.728 -.727a3.088 3.088 0 1 0 -4.367 4.367l5.095 5.096z" />
                                    </Svg>
                                    <Text style={styles.text}>
                                        Familienstand
                                    </Text>
                                </View>
                                <View style={styles.infoCol}>
                                    <Text style={styles.text}>
                                        {resumeContent.step1.status ===
                                            "single" && "Ledig"}
                                        {resumeContent.step1.status ===
                                            "married" && "Verheiratet"}
                                    </Text>
                                </View>
                            </View>
                        )}
                        {typeof resumeContent.step1.numberOfChildren ===
                            "number" && (
                            <View style={styles.infoRow}>
                                <View style={styles.infoTitleCol}>
                                    <Svg
                                        viewBox="0 0 24 24"
                                        width="14"
                                        height="14"
                                        stroke="#333333"
                                        strokeWidth={2}
                                    >
                                        <Path d="M3.5 17.5c5.667 4.667 11.333 4.667 17 0" />
                                        <Path d="M19 18.5l-2 -8.5l1 -2l2 1l1.5 -1.5l-2.5 -4.5c-5.052 .218 -5.99 3.133 -7 6h-6a3 3 0 0 0 -3 3" />
                                        <Path d="M5 18.5l2 -9.5" />
                                        <Path d="M8 20l2 -5h4l2 5" />
                                    </Svg>
                                    <Text style={styles.text}>Kinder</Text>
                                </View>
                                <View style={styles.infoCol}>
                                    <Text style={styles.text}>
                                        {resumeContent.step1.numberOfChildren ||
                                            "Keine Kinder"}
                                    </Text>
                                </View>
                            </View>
                        )}
                    </View>
                </View>
                {resumeContent.step3.photo && (
                    <View style={styles.imageContainer}>
                        <View
                            style={{
                                ...styles.branding,
                                backgroundColor: resumeContent.step3.color,
                            }}
                        />
                        <Image
                            style={styles.image}
                            src={resumeContent.step3.photo}
                        />
                    </View>
                )}
            </View>

            {resumeContent.step2.experienceData.length > 0 && (
                <View style={styles.section}>
                    <View wrap={false}>
                        <Text style={styles.title}>Berufserfahrung</Text>
                        {resumeContent.step2.experienceData[0] && (
                            <Experience
                                experience={
                                    resumeContent.step2.experienceData[0]
                                }
                            />
                        )}
                    </View>
                    {resumeContent.step2.experienceData
                        .slice(1)
                        .map((exp) => (
                            <Experience
                                key={JSON.stringify(exp)}
                                experience={exp}
                            />
                        ))}
                </View>
            )}

            {resumeContent.step2.trainingData.length > 0 && (
                <View style={styles.section}>
                    <View wrap={false}>
                        <Text style={styles.title}>Aus- & Weiterbildung</Text>
                        {resumeContent.step2.trainingData[0] && (
                            <Experience
                                experience={resumeContent.step2.trainingData[0]}
                            />
                        )}
                    </View>
                    {resumeContent.step2.trainingData
                        .slice(1)
                        .map((training) => (
                            <Experience
                                key={JSON.stringify(training)}
                                experience={training}
                            />
                        ))}
                </View>
            )}

            {(
                resumeContent.step2.additionalSkills?.length > 0 ||
                resumeContent.step2.softwareSkills?.length > 0 ||
                resumeContent.step2.drivingLicenses?.length > 0 ||
                resumeContent.step2.hobbies?.length > 0 ||
                resumeContent.step2.languages?.length
            ) && (
                <SonstigesSection step2={resumeContent.step2} />
            )}
            <Text
                style={styles.pageCount}
                render={({ pageNumber, totalPages }) =>
                    totalPages > 1 && `${pageNumber} / ${totalPages}`
                }
                fixed={true}
            />
        </Page>
    </Document>
);

export { ResumePDF };
